import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {
  FormControl,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  IconButton,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import Iconify from 'src/minimal-components/iconify';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, useForm} from 'react-hook-form';
import {useOnlineContext} from './OnlineTestContext';
import uuidv4 from 'src/utils/uuidv4';

interface Choice {
  id: string;
  OptionText: string;
  is_single_multiple_trueOrFalse_correct_option: boolean;
}

interface MultipleQuestionTypeProps {
  questionName?: string;
  choicesName?: string;
  correctAnswerName?: string;
  handleEditQuestion?: any;
}

interface ChildFormMethods {
  submitForm: () => Promise<any>;
}
const schema = yup.object().shape({
  QuestionText: yup.string().required('Question is required'),
  choices: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        is_single_multiple_trueOrFalse_correct_option: yup.boolean(),
        OptionText: yup.string().required('Choice is required')
      })
    )
    .min(2, 'At least two choices are required')
    .test(
      'at-least-one-true',
      'At least one choice must be marked as correct',
      choices =>
        choices?.some(
          choice =>
            choice.is_single_multiple_trueOrFalse_correct_option === true
        )
    )
});
const MultipleTypeQuestion = forwardRef<
  ChildFormMethods,
  MultipleQuestionTypeProps
>(({questionName, choicesName, correctAnswerName, handleEditQuestion}, ref) => {
  const [choices, setChoices] = useState<Choice[]>(
    handleEditQuestion?.QuestionType_id === 2
      ? handleEditQuestion?.choices || [
          {
            id: 1,
            OptionText: '',
            is_single_multiple_trueOrFalse_correct_option: false
          },
          {
            id: 2,
            OptionText: '',
            is_single_multiple_trueOrFalse_correct_option: false
          }
        ]
      : [
          {
            id: 1,
            OptionText: '',
            is_single_multiple_trueOrFalse_correct_option: false
          },
          {
            id: 2,
            OptionText: '',
            is_single_multiple_trueOrFalse_correct_option: false
          }
        ]
  );
  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    trigger,
    getValues,
    formState: {errors}
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      QuestionText: handleEditQuestion?.QuestionText || '',
      choices: choices
    }
  });
  const {onlineData, setOnlineData} = useOnlineContext();
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  useEffect(() => {
    setValue('choices', choices);
    // const ValidationError = async () => await trigger('choices');
    // ValidationError();
  }, [choices]);
  useImperativeHandle(ref, () => ({
    submitForm: () =>
      new Promise((resolve, reject) => {
        handleSubmit(data => {
          resolve(data); // Resolve the promise with form data
        })();
      })
  }));
  const choicesValue = watch('choices');
  const generateUniqueId = (): string => {
    const currentIds = [
      ...(Array.isArray(onlineData?.Ipaddress)
        ? onlineData.Ipaddress.map((data: {id: any}) =>
            typeof data.id === 'string' ? data.id : ''
          )
        : []),
      ...(Array.isArray(choicesValue)
        ? choicesValue.map((field: {id: string}) => field.id)
        : [])
    ];
    if (currentIds.length > 0) {
      const maxId = Math.max(...currentIds);
      return uuidv4();
    }

    return uuidv4(); // Default to 1 if the array is empty or undefined
  };

  const handleAddChoice = async () => {
    const newId = generateUniqueId();
    if (setChoices && choices) {
      const newChoice = {
        id: newId,
        OptionText: '',
        is_single_multiple_trueOrFalse_correct_option: false
      };
      setChoices([...choices, newChoice]);
    }
    await trigger('choices');
  };

  // Handle deleting a choice
  const handleDeleteChoice = async (id: string) => {
    if (setChoices && choices) {
      const valuess = choices.filter(
        (choice: {id: string}) => choice.id !== id
      );
      setChoices(valuess);
    }
    // const correctChoice = watch('correctAnswer');
    // if (string(correctChoice) === id) {
    //   setValue('correctAnswer', 1);
    // }
    await trigger('choices');
  };
  return (
    <Card variant="outlined" sx={{mx: 'auto', padding: '1vh'}}>
      <Box>
        <Typography variant="h1" sx={{mt: 2, mb: 1}}>
          Question
        </Typography>
        <Controller
          name="QuestionText"
          control={control}
          render={({field}) => (
            <TextField
              {...field}
              fullWidth
              label="Write Question Here"
              variant="outlined"
              error={!!errors['QuestionText']}
              helperText={errors['QuestionText']?.message}
              sx={{mb: 2}}
            />
          )}
        />
      </Box>
      <Box>
        <FormControl component="fieldset">
          <Typography variant="h6" sx={{mb: 1}}>
            Answers
          </Typography>
          <Controller
            name="choices"
            control={control}
            render={({field}) => {
              return (
                <Box>
                  {choices.map(
                    (
                      choice: {
                        id: string;
                        OptionText: string;
                        is_single_multiple_trueOrFalse_correct_option: boolean;
                      },
                      index: number
                    ) => {
                      return (
                        <Box
                          key={choice.id}
                          sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                          <Controller
                            name={`choices.${index}.is_single_multiple_trueOrFalse_correct_option`}
                            control={control}
                            render={({field}) => (
                              <Checkbox
                                checked={field.value}
                                onChange={e => {
                                  const updatedChoices = choices.map(
                                    (item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            is_single_multiple_trueOrFalse_correct_option:
                                              e.target.checked
                                          }
                                        : item
                                  );

                                  if (setChoices) {
                                    setChoices(updatedChoices);
                                  }
                                  trigger('choices');

                                  field.onChange(e.target.checked); // Update form state with the checkbox value
                                }}
                              />
                            )}
                          />
                          <Controller
                            name={`choices.${index}.OptionText`}
                            control={control}
                            render={({field: textField}) => (
                              <TextField
                                {...textField}
                                fullWidth
                                variant="outlined"
                                value={choice.OptionText}
                                onMouseEnter={() => setHoveredItemId(choice.id)}
                                onMouseLeave={() => setHoveredItemId(null)}
                                onChange={e => {
                                  const updatedChoices = choices.map(
                                    (item, i) =>
                                      i === index
                                        ? {
                                            ...item,
                                            OptionText: e.target.value
                                          }
                                        : item
                                  );
                                  if (setChoices) {
                                    setChoices(updatedChoices);
                                  }
                                  textField.onChange(e.target.value);
                                }}
                                onBlur={textField.onBlur}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() =>
                                          handleDeleteChoice(choice.id)
                                        }
                                        edge="end"
                                        sx={{
                                          color: 'error.main',
                                          opacity:
                                            hoveredItemId === choice.id ? 1 : 0
                                        }}>
                                        <Iconify icon="solar:trash-bin-trash-bold" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                error={!!errors['choices']?.[index]?.OptionText}
                                helperText={
                                  errors['choices']?.[index]?.OptionText
                                    ?.message
                                }
                              />
                            )}
                          />
                        </Box>
                      );
                    }
                  )}

                  {/* Display validation errors */}
                </Box>
              );
            }}
          />
          {errors && (
            <FormHelperText error sx={{px: 2}}>
              {errors?.choices?.root?.message}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
      <Button color="primary" onClick={handleAddChoice} sx={{mt: 2, mb: 2}}>
        Add Choice
      </Button>
    </Card>
  );
});

export default MultipleTypeQuestion;
